import { ChevronRightIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Frame, FrameHeader, FramePanel, FrameTitle, FrameAction } from "@/components/ui/frame";
import { cn } from "@/lib/utils";
import { GOAL_LABEL, TONE_TEXT, VERDICT, clientHealth, clientMailboxes, fmtAge, fmtUnit, paceOf, pct, supplyOf, type Tone } from "./format";
import { Hint, ToneBadge } from "./parts";
import type { ClientState, GoalState, MailboxState } from "./types";

/** One managed client: health, goal against pace, supply, mailboxes. Details open in the sheet. */
export function ClientCard({ client, onOpen }: { client: ClientState; onOpen: () => void }) {
  const health = clientHealth(client);
  return (
    <Frame className="h-full">
      <FrameHeader>
        <FrameTitle className="truncate">{client.name}</FrameTitle>
        <FrameAction>
          <ToneBadge tone={health.tone}>{health.label}</ToneBadge>
        </FrameAction>
      </FrameHeader>
      <FramePanel className="@container flex flex-1 flex-col gap-4">
        {client.org && client.goal ? <Overview client={client} goal={client.goal} /> : <Unavailable client={client} />}
        <div className="mt-auto flex items-center justify-between gap-2 border-t pt-3">
          <span className="truncate text-xs text-muted-foreground">
            {client.org
              ? `${client.org.activated_used}/${client.org.activated_limit ?? "∞"} approvals · ${client.campaigns.filter((c) => c.running).length} running${client.queue?.open ? ` · ${client.queue.open} to decide` : ""}`
              : `${client.slug} plan`}
          </span>
          <Button variant="ghost" size="sm" onClick={onOpen} className="-mr-1.5 shrink-0">
            Details
            <ChevronRightIcon />
          </Button>
        </div>
      </FramePanel>
    </Frame>
  );
}

function Overview({ client, goal }: { client: ClientState; goal: GoalState }) {
  const supply = supplyOf(client);
  const staleHandoff = client.handoffs.some((h) => (h.age_hours ?? 0) >= 24);
  const oldest = client.handoffs.reduce<number | null>((max, h) => (h.age_hours !== null && (max === null || h.age_hours > max) ? h.age_hours : max), null);
  const showHandoffs = client.plan?.show_handoffs ?? true;

  return (
    <>
      <Goal goal={goal} />
      {/* 2×2 in a narrow card, one row once the card is wide enough for the labels. */}
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border bg-border @xs:grid-cols-4">
        <Metric
          label="Queue"
          hint="Creators still waiting for a cold email across running campaigns"
          value={supply.running.length ? String(supply.queue) : "—"}
          tone={supply.running.length > 0 && supply.queue === 0 ? "warning" : undefined}
        />
        <Metric
          label="Runway"
          hint="Days of cold emails left at the configured limits — the shortest running campaign"
          value={supply.blocked ? "blocked" : supply.runway === null ? "—" : `${supply.runway} d`}
          tone={supply.blocked || (supply.runway !== null && supply.runway < 3) ? "warning" : undefined}
        />
        <Metric
          label="Follow-ups"
          hint="First follow-ups owed: never-replied creators whose wait elapsed and who were not nudged yet"
          value={supply.running.length ? String(supply.followups) : "—"}
        />
        <Metric
          label="Handoffs"
          hint={showHandoffs ? `Questions waiting for the team${oldest !== null ? ` — oldest ${fmtAge(oldest)}` : ""}` : "Off for this client: its Tasks page is worked directly"}
          value={showHandoffs ? String(client.handoffs.length) : "off"}
          tone={staleHandoff ? "warning" : showHandoffs ? undefined : "muted"}
        />
      </div>
      <Mailboxes mailboxes={clientMailboxes(client.campaigns)} />
    </>
  );
}

function Goal({ goal }: { goal: GoalState }) {
  const pace = paceOf(goal);
  // A rolling window is always "the last N days": no day counter, and the pace line is the whole target.
  const rolling = goal.anchor === "rolling";
  const expected = rolling ? `${fmtUnit(goal, goal.expected_now ?? 0)} per ${goal.window_days} days` : `${fmtUnit(goal, goal.expected_now ?? 0)} expected by today`;
  const fill = goal.target ? Math.min(100, (goal.progress / goal.target) * 100) : 0;
  const marker = goal.target && goal.expected_now !== null ? Math.min(100, (goal.expected_now / goal.target) * 100) : null;
  const targetHint =
    goal.target_source === "grade_funding" ? "Target = the deposited wallet total." : goal.target ? "Target from the plan." : "No target: throughput only, no pace alarm.";

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-2 text-xs text-muted-foreground">
        <Hint text={`${goal.basis}. ${targetHint}`}>{GOAL_LABEL[goal.kind]}</Hint>
        {rolling ? (
          <span className="shrink-0">last {goal.window_days} days</span>
        ) : goal.day !== null ? (
          <span className="shrink-0 font-mono">
            day {Math.min(goal.day, goal.window_days)}/{goal.window_days}
          </span>
        ) : null}
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="font-mono text-2xl font-semibold tabular-nums">{fmtUnit(goal, goal.progress)}</span>
        {goal.target ? <span className="font-mono text-sm text-muted-foreground">/ {fmtUnit(goal, goal.target)}</span> : null}
      </div>
      {goal.target ? (
        <div className="relative h-2 w-full rounded-pill bg-muted" role="img" aria-label={`${Math.round(fill)}% of target`}>
          <div className={cn("h-full rounded-pill", pace.kind === "behind" ? "bg-warning" : "bg-success")} style={{ width: `${fill}%` }} />
          {marker !== null && marker < 100 && (
            <div className="absolute -top-1 h-4 w-0.5 rounded-pill bg-foreground/60" style={{ left: `calc(${marker}% - 1px)` }} aria-hidden />
          )}
        </div>
      ) : null}
      <p className={cn("text-xs", pace.kind === "behind" ? "text-warning" : "text-muted-foreground")}>
        {pace.kind === "behind"
          ? `${fmtUnit(goal, pace.short)} short of ${expected}`
          : pace.kind === "on_pace"
            ? `On pace — ${expected}`
            : pace.kind === "not_started"
              ? "The window starts with the first outreach"
              : "No target — throughput only"}
        {goal.progress_window !== goal.progress && <> · {fmtUnit(goal, goal.progress_window)} in this window</>}
      </p>
    </div>
  );
}

function Metric({ label, hint, value, tone }: { label: string; hint: string; value: string; tone?: Tone }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5 bg-background px-2.5 py-2">
      <span className="truncate text-[11px] text-muted-foreground">
        <Hint text={hint}>{label}</Hint>
      </span>
      <span className={cn("truncate font-mono text-sm font-medium tabular-nums", tone && TONE_TEXT[tone])}>{value}</span>
    </div>
  );
}

const VERDICT_ORDER: MailboxState["verdict"][] = ["weak", "ok", "immature", "idle"];

function Mailboxes({ mailboxes }: { mailboxes: MailboxState[] }) {
  const weak = mailboxes.filter((m) => m.verdict === "weak");
  const off = mailboxes.filter((m) => !m.enabled).length;
  return (
    <div className="flex flex-col gap-1.5 text-xs">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="text-muted-foreground">Mailboxes</span>
        {mailboxes.length === 0 && <span className="text-muted-foreground">none on a running campaign</span>}
        {VERDICT_ORDER.map((v) => {
          const n = mailboxes.filter((m) => m.verdict === v).length;
          if (!n) return null;
          return (
            <Hint key={v} text={VERDICT[v].hint} underline={false}>
              <span className="inline-flex items-center gap-1.5">
                <span className={cn("size-1.5 rounded-full", VERDICT[v].dot)} />
                <span className="font-mono">{n}</span>
                {VERDICT[v].label}
              </span>
            </Hint>
          );
        })}
        {off > 0 && <span className="text-muted-foreground">· {off} switched off</span>}
      </div>
      {weak.map((m) => (
        <div key={m.account_id} className="flex min-w-0 items-center gap-1.5 text-destructive">
          <span className="truncate">{m.email ?? m.account_id}</span>
          <span className="shrink-0 font-mono">{pct(m.matured_rate)}</span>
        </div>
      ))}
    </div>
  );
}

function Unavailable({ client }: { client: ClientState }) {
  if (client.plan_error) {
    return <p className="wrap-break-word font-mono text-xs text-destructive">{client.plan_error}</p>;
  }
  if (!client.org_id) {
    return (
      <div className="flex flex-col gap-2 text-sm">
        <p className="text-muted-foreground">
          No org set for <span className="font-mono">{client.env}</span> on the <span className="font-mono">{client.slug}</span> plan.
          {client.org_candidates.length > 0 ? " Candidates from campaign names:" : " No candidate org found."}
        </p>
        {client.org_candidates.length > 0 && (
          <ul className="flex flex-col gap-1.5">
            {client.org_candidates.map((c) => (
              <li key={c.org_id} className="flex min-w-0 flex-col">
                <code className="w-fit rounded bg-muted px-1 py-0.5 text-xs">{c.org_id}</code>
                <span className="truncate text-xs text-muted-foreground">{c.campaign_names.join(", ")}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }
  const missing = client.alerts.find((a) => a.code === "org_missing");
  return <p className="text-sm text-muted-foreground">{missing?.text ?? "No data for this client yet."}</p>;
}
